import { useFormContext } from "react-hook-form";

import { logger } from "c7-logger";

import { usePaymentOptionContext } from "checkout-experience/components/payment-method/payment-option-provider";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import { ChexFieldValues } from "checkout-query-client/types/chex-form-fields";

interface UsePlacesServiceV2Props {
  language: string;
  apiHost: string;
}

interface GetPlacePredictionsProps {
  input: string;
}

interface GetPlaceDetailsProps {
  place_id: string;
}

interface GetPlaceDetailsResponse {
  html_attributions?: string[];
  result: google.maps.places.PlaceResult;
  status: string;
}

export function usePlacesService({
  language,
  apiHost,
}: UsePlacesServiceV2Props) {
  const { checkoutSessionToken } = useChexContext();
  const { getValues } = useFormContext();
  const { paymentOption } = usePaymentOptionContext() ?? {};
  const { billingAddressCountry } = (getValues(`${paymentOption?.formId}`) ||
    {}) as ChexFieldValues;

  async function getPlacePredictions({
    input,
  }: GetPlacePredictionsProps): Promise<google.maps.places.AutocompleteResponse> {
    const hostName = `${apiHost}/googleapis/maps/api/place/autocomplete/json`;

    try {
      const response = await fetch(
        `${hostName}?input=${input}&components=country:${billingAddressCountry}&type=address&language=${language}`,
        {
          headers: { Authorization: `Bearer ${checkoutSessionToken}` },
        }
      );
      return (await response.json()) as google.maps.places.AutocompleteResponse;
    } catch (e) {
      logger.error(e);
      return { predictions: [] };
    }
  }

  async function getPlaceDetails({
    place_id,
  }: GetPlaceDetailsProps): Promise<google.maps.places.PlaceResult | null> {
    try {
      const hostName = `${apiHost}/googleapis/maps/api/place/details/json`;

      const response = await fetch(
        `${hostName}?place_id=${place_id}&region=${billingAddressCountry}&language=${language}`,
        {
          headers: { Authorization: `Bearer ${checkoutSessionToken}` },
        }
      );

      if (response.status !== 200) {
        return null;
      }

      const data = (await response.json()) as GetPlaceDetailsResponse;

      return data.result;
    } catch (e) {
      logger.error(e);
      return null;
    }
  }

  return {
    getPlacePredictions,
    getPlaceDetails,
  };
}
